import { useReducer, useState } from "react"

import { ManualOrder } from "@/types"

export const MANUAL_ORDER_FORM_STEPS = [
  { key: "order_type", label: "Typ zlecenia" },
  { key: "pickup_data", label: "Dane odbioru" },
  { key: "delivery_data", label: "Dane dostawy" },
  { key: "completion_time", label: "Czas kompletacji" },
  { key: "parameters", label: "Parametry" },
  { key: "remaining", label: "Pozostałe" },
  { key: "summary", label: "Podsumowanie" },
] as const

export type ManualOrderFormStepKey = typeof MANUAL_ORDER_FORM_STEPS[number]["key"]
export type ManualOrderFormStepLabel = typeof MANUAL_ORDER_FORM_STEPS[number]["label"]

export type ManualOrderKind = ManualOrder["delivery_method"]

export type ManualOrderFormData = {
  values: Partial<ManualOrder>
}

const defaultFormState: ManualOrderFormData["values"] = {
  price_delivery_subunit: 0,
  automatic_confirmation: true,
  delivery_method: "delivery_now",
  type: "Order::Manual",
}

export function useManualOrderForm() {
  function reducer(state: ManualOrderFormData, action: { type: string; payload: ManualOrderFormData["values"] }) {
    switch (action.type) {
      case "SET_FORM_STATE_VALUE":
        return { ...state, values: { ...state.values, ...action.payload } }
      case "RESET_FORM_STATE":
        return { ...state, values: { ...action.payload } }
      default:
        return state
    }
  }

  const [orderKind, setOrderKind] = useState<ManualOrderKind>()
  const [formStep, setFormStep] = useState<ManualOrderFormStepKey>("order_type")
  const [formState, dispatch] = useReducer(reducer, { values: defaultFormState })

  const allowedSteps = MANUAL_ORDER_FORM_STEPS.filter(
    (step) =>
      !(step.key === "pickup_data" && orderKind === "delivery_only_now") &&
      !(step.key === "delivery_data" && orderKind === "pickup_now")
  )

  const setNextStep = () => {
    setFormStep(allowedSteps[allowedSteps.findIndex((step) => step.key === formStep)! + 1].key)
  }

  const setPreviousStep = () => {
    setFormStep(allowedSteps[allowedSteps.findIndex((step) => step.key === formStep)! - 1].key)
  }

  const setFormStateValue = (payload: ManualOrderFormData["values"]) => {
    dispatch({ type: "SET_FORM_STATE_VALUE", payload })
  }

  const resetForm = () => {
    dispatch({ type: "RESET_FORM_STATE", payload: defaultFormState })
    setFormStep("order_type")
  }

  return {
    orderKind,
    formStep,
    formState,
    allowedSteps,
    setOrderKind,
    setNextStep,
    setPreviousStep,
    setFormStateValue,
    resetForm,
  }
}
